import { useQuery } from '@apollo/client';
import { UserDto } from '@ecdlink/core';
import debounce from 'lodash.debounce';
import { UserList, getUserCount } from '@ecdlink/graphql';
import { SearchDropDownOption, Table } from '@ecdlink/ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Status } from '../application-admins/applications-admins.types';
import { format } from 'date-fns';

import { TableRefMethods } from '@ecdlink/ui/lib/components/table/types';
import { columnColor } from '../../../../utils/app-usage/app-usage-utils';
import { ColumnNames, UserSearch } from './application-user.types';
import {
  TablePagination,
  TABLE_ROWS_PER_PAGE,
} from '../../components/table-pagination/table-pagination';

const PAGE_SIZE = 20;

export const sortByClientStatusOptions: SearchDropDownOption<string>[] = [
  Status?.ACTIVE,
  Status?.INACTIVE,
].map((item) => ({
  id: item,
  label: item,
  value: item,
}));

export default function ApplicationUsers() {
  const [tableData, setTableData] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const tableRef = useRef<TableRefMethods>(null);

  const onChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const [statusFilter, setStatusFilter] = useState<
    SearchDropDownOption<string>[]
  >([sortByClientStatusOptions[0]]);

  // Filters shared between the table's paged query and the total-count query.
  const filterVariables = useMemo(() => {
    const filterBy: {
      fieldName: string;
      filterType: string;
      value: string;
    }[] = [];

    if (statusFilter?.length === 1) {
      filterBy.push({
        fieldName: 'isActive',
        filterType: 'EQUALS',
        value: statusFilter[0].value === Status?.ACTIVE ? 'true' : 'false',
      });
    }

    if (startDate) {
      filterBy.push({
        fieldName: 'insertedDate',
        filterType: 'GREATER_THAN_OR_EQUAL',
        value: startDate.toISOString(),
      });
    }

    if (endDate) {
      filterBy.push({
        fieldName: 'insertedDate',
        filterType: 'LESS_THAN_OR_EQUAL',
        value: endDate.toISOString(),
      });
    }

    return { search: searchValue, filterBy };
  }, [endDate, searchValue, startDate, statusFilter]);

  // Whenever a filter or the search term changes, jump back to page 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [filterVariables]);

  const queryVariables = useMemo(
    () => ({
      search: filterVariables.search,
      pagingInput: {
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
        filterBy: filterVariables.filterBy,
      },
      order: [
        {
          insertedDate: 'DESC',
        },
      ],
    }),
    [filterVariables, currentPage]
  );

  const { data, loading } = useQuery(UserList, {
    variables: queryVariables,
    fetchPolicy: 'network-only',
  });

  const { data: countData, loading: countLoading } = useQuery(getUserCount, {
    variables: {
      search: filterVariables.search,
      pagingInput: { filterBy: filterVariables.filterBy },
    },
    fetchPolicy: 'network-only',
  });

  const totalCount = countData?.countUsers ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const goToPage = (page: number) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
    tableRef?.current?.resetSelectedRows();
  };

  const isLoading = loading;
  const isFilterActive = !!startDate || !!endDate || !!statusFilter?.length;

  const noContentText = useMemo(() => {
    if (isFilterActive) {
      return 'No results found. Try changing the filters selected';
    }

    return 'No entries found';
  }, [isFilterActive]);

  const search = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value || '');
  }, 150);

  const mapUserTableItem = (item: any) => {
    return {
      ...item,
      displayColumnIdPassportEmail:
        item?.user?.userName ?? item?.idNumber ?? item?.email ?? '',
      userId: item.id,
      fullName: `${item.fullName}`,
      isActive: item.isActive,
      idNumber: item.idNumber,
      dateInvited: item.insertedDate,
      connectUsage: item?.connectUsage,
    };
  };

  useEffect(() => {
    if (data?.users) {
      setTableData(data.users.map((item: UserDto) => mapUserTableItem(item)));
    }
  }, [data]);

  const clearFilters = () => {
    setStatusFilter([]);
    setStartDate('');
    setEndDate('');
  };

  const columns: Icolumn[] = [
    {
      field: 'displayColumnIdPassportEmail',
      use: ColumnNames.IdPassportEmail,
    },
    {
      field: 'fullName',
      use: ColumnNames.Name,
    },
    {
      field: 'roleComponent',
      use: ColumnNames.Role,
    },
    {
      field: 'insertedDateFormatted',
      use: ColumnNames.Date,
    },
    {
      field: 'isActiveComponent',
      use: ColumnNames.Status,
    },
  ];

  const rows: Irow[] =
    tableData?.map((item) => ({
      ...item,
      key: item?.id,
      displayColumnIdPassportEmail:
        item?.userName ?? item?.idNumber ?? item?.email ?? '-',
      connectUsageComponent: item?.connectUsage
        ? columnColor(item.connectUsage)
        : '-',
      insertedDateFormatted: item?.insertedDate
        ? format(new Date(item?.insertedDate), 'dd/MM/yyyy')
        : '-',
      isActiveComponent: (
        <p className={item?.isActive ? 'text-successMain' : 'text-errorMain'}>
          {item?.isActive ? 'Active' : 'Inactive'}
        </p>
      ),
      roleComponent: (
        <div className="ml-0 flex cursor-pointer items-center">
          {item?.roles?.map((item: any) => {
            const chipColor = (role?: string) => {
              switch (role) {
                case 'Administrator':
                  return 'bg-secondary';
                case 'Practitioner':
                  return 'bg-infoMain';
                case 'Principal':
                  return 'bg-tertiary';
                default:
                  return 'bg-primary';
              }
            };
            return (
              <div
                key={`role_` + item?.id}
                className={
                  `${chipColor(item?.tenantName)}` +
                  ' m-1 rounded-full p-3 py-1 text-xs text-white'
                }
              >
                {item?.tenantName}
              </div>
            );
          })}
        </div>
      ),
    })) ?? [];

  return (
    <div className="bg-adminPortalBg h-full rounded-2xl p-4 ">
      <div className="rounded-xl bg-white p-12 [&_tbody_td]:py-2">
        <Table
          watchMode
          ref={tableRef}
          rows={rows}
          columns={columns}
          rowsPerPage={TABLE_ROWS_PER_PAGE}
          onClearFilters={clearFilters}
          noContentText={noContentText}
          loading={{
            isLoading: tableData === undefined || isLoading,
            size: 'medium',
            spinnerColor: 'adminPortalBg',
            backgroundColor: 'secondary',
          }}
          search={{
            placeholder: UserSearch.SearchBy,
            onChange: search,
          }}
          filters={[
            {
              dateFormat: 'd MMM yyyy',
              className: 'w-64 h-11 mt-1 border-2 border-transparent',
              isFullWidth: false,
              colour: !!startDate ? 'secondary' : 'adminPortalBg',
              textColour: !!startDate ? 'white' : 'textMid',
              placeholderText: ColumnNames.Date,
              type: 'date-picker',
              showChevronIcon: true,
              chevronIconColour: !!startDate ? 'white' : 'primary',
              hideCalendarIcon: true,
              selected: startDate,
              onChange,
              startDate,
              endDate,
              selectsRange: true,
              shouldCloseOnSelect: true,
            },
            {
              type: 'search-dropdown',
              menuItemClassName: 'w-11/12 left-4',
              className: 'h-11 mb-2 border-2 border-transparent',
              color: 'quatenary',
              bgColor: 'white',
              options: sortByClientStatusOptions,
              selectedOptions: statusFilter,
              onChange: setStatusFilter,
              placeholder: 'Status',
              multiple: true,
              info: { name: 'Status:' },
            },
          ]}
        />
        {rows.length > 0 && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            countLoading={countLoading}
            isLoading={isLoading}
            onPageChange={goToPage}
          />
        )}
      </div>
    </div>
  );
}
